import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const getToken = () => localStorage.getItem('token');
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const SOURCE_COLORS = {
  Facebook:    'bg-blue-100 text-blue-700',
  Instagram:   'bg-pink-100 text-pink-700',
  OLX:         'bg-teal-100 text-teal-700',
  Twitter:     'bg-sky-100 text-sky-700',
  Institution: 'bg-purple-100 text-purple-700',
  Website:     'bg-indigo-100 text-indigo-700',
  Google:      'bg-yellow-100 text-yellow-700',
  Whatsapp:    'bg-green-100 text-green-700',
  Other:       'bg-gray-100 text-gray-600',
};

const STATUS_OPTIONS = ['Pending', 'Called', 'Not Reachable', 'Call Later', 'Registration Completed', 'Rejected'];

const STATUS_META = {
  'Pending':                { color: 'bg-gray-100 text-gray-600 border-gray-300',       dot: 'bg-gray-400'   },
  'Called':                 { color: 'bg-blue-100 text-blue-700 border-blue-300',        dot: 'bg-blue-500'   },
  'Not Reachable':          { color: 'bg-orange-100 text-orange-700 border-orange-300',  dot: 'bg-orange-400' },
  'Call Later':             { color: 'bg-yellow-100 text-yellow-700 border-yellow-300',  dot: 'bg-yellow-500' },
  'Registration Completed': { color: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500' },
  'Rejected':               { color: 'bg-red-100 text-red-700 border-red-300',           dot: 'bg-red-500'    },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META['Pending'];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-bold ${m.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {status || 'Pending'}
    </span>
  );
};

const fmtDateTime = (dt) => {
  if (!dt) return '—';
  try {
    return new Date(dt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return dt; }
};

const getTab = (status) => {
  if (status === 'Rejected') return 'rejected';
  if (status === 'Registration Completed') return 'registered';
  return 'pending';
};

const HRExecutiveDashboard = ({ name, API_URL: propApiUrl }) => {
  const API_URL = propApiUrl || process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch]     = useState('');
  const [updating, setUpdating] = useState(null);
  const [toast, setToast]       = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff/hr-executive/leads`, { headers: authHeader() });
      setLeads(res.data || []);
    } catch { setLeads([]); } finally { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await axios.put(
        `${API_URL}/api/staff/hr-executive/leads/${id}/status`,
        { status },
        { headers: authHeader() }
      );
      setLeads(p => p.map(l => l.id === id ? res.data.lead : l));
      showToast('Status updated');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update status');
    } finally { setUpdating(null); }
  };

  // Counts per tab
  const counts = {
    pending:    leads.filter(l => getTab(l.status) === 'pending').length,
    registered: leads.filter(l => getTab(l.status) === 'registered').length,
    rejected:   leads.filter(l => getTab(l.status) === 'rejected').length,
  };

  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const matchTab = getTab(l.status) === activeTab;
    const matchSearch = !q || l.name?.toLowerCase().includes(q) || l.contact_number?.includes(q) || l.data_from?.toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  const TABS = [
    { key: 'pending',    label: 'Pending',    color: 'text-gray-700',    active: 'border-black bg-black text-white' },
    { key: 'registered', label: 'Registered', color: 'text-emerald-700', active: 'border-emerald-600 bg-emerald-600 text-white' },
    { key: 'rejected',   label: 'Rejected',   color: 'text-red-600',     active: 'border-red-600 bg-red-600 text-white' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-xl bg-black px-5 py-3 text-sm font-bold text-white shadow-xl">{toast}</div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-black text-black">HR Executive Dashboard</h1>
          <p className="text-sm text-gray-500">Welcome, {name}</p>
        </div>
        <button onClick={fetchLeads} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-yellow-400 transition-colors">
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-3xl font-black text-black">{counts.pending}</div>
          <div className="text-sm font-semibold text-gray-500">Pending</div>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
          <div className="text-3xl font-black text-emerald-700">{counts.registered}</div>
          <div className="text-sm font-semibold text-emerald-600">Registration Completed</div>
        </div>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm">
          <div className="text-3xl font-black text-red-600">{counts.rejected}</div>
          <div className="text-sm font-semibold text-red-500">Rejected</div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`rounded-2xl border px-5 py-2.5 text-sm font-bold transition-all ${activeTab === t.key ? t.active : `border-gray-300 bg-white ${t.color} hover:border-gray-400`}`}>
              {t.label} <span className="ml-1 opacity-70">({counts[t.key]})</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, contact or source…"
            className="w-full rounded-2xl border border-gray-300 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200" />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-black text-yellow-400">
              <th className="px-4 py-3 text-left font-bold">S.No</th>
              <th className="px-4 py-3 text-left font-bold">Name</th>
              <th className="px-4 py-3 text-left font-bold">Contact Number</th>
              <th className="px-4 py-3 text-left font-bold">Data From</th>
              <th className="px-4 py-3 text-left font-bold">Forwarded On</th>
              <th className="px-4 py-3 text-left font-bold">Status</th>
              <th className="px-4 py-3 text-center font-bold">Update Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-10 text-center text-gray-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="py-10 text-center text-gray-400">No leads in {activeTab} tab</td></tr>
            ) : filtered.map((lead, idx) => (
              <tr key={lead.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 font-semibold text-gray-400">{idx + 1}</td>
                <td className="px-4 py-3 font-bold text-black">{lead.name}</td>
                <td className="px-4 py-3 font-mono text-gray-700">{lead.contact_number}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${SOURCE_COLORS[lead.data_from] || SOURCE_COLORS.Other}`}>
                    {lead.data_from}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmtDateTime(lead.forwarded_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={lead.status || 'Pending'} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-center">
                    <select
                      value={lead.status || 'Pending'}
                      onChange={e => updateStatus(lead.id, e.target.value)}
                      disabled={updating === lead.id}
                      className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-black outline-none focus:border-yellow-400 disabled:opacity-50 cursor-pointer"
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {updating === lead.id && <span className="ml-2 text-xs text-gray-400 self-center">Saving…</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="border-t border-gray-100 px-4 py-2 text-right text-xs text-gray-400">
            Showing {filtered.length} of {leads.filter(l => getTab(l.status) === activeTab).length} in {activeTab}
          </div>
        )}
      </div>
    </div>
  );
};

export default HRExecutiveDashboard;
